import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { useUI } from 'contexts/UIContext';
import { Button } from 'components/ui';
import Modal from 'components/Modal';
import ProfileSection from './components/ProfileSection';
import SubscriptionInfo from './components/SubscriptionInfo';
import AccountActions from './components/AccountActions';
import { uploadAvatar, deleteAvatar } from 'lib/supabase-db';
import { supabase } from 'config/supabase';
import { Subscription } from 'types';

interface ProfileData {
  firstName: string;
  lastName: string;
  companyName: string;
}

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { user, signOut, updateUserMetadata } = useAuth();
  const { showToast } = useUI();
  const [profileData, setProfileData] = useState<ProfileData>({
    firstName: '',
    lastName: '',
    companyName: '',
  });
  const [avatarFile, setAvatarFile] = useState<File | undefined>();
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [outputsUsed, setOutputsUsed] = useState<number>(0);

  useEffect(() => {
    const loadData = async () => {
      if (!user) return;

      try {
        // Load profile data
        setProfileData({
          firstName: user.user_metadata?.first_name || '',
          lastName: user.user_metadata?.last_name || '',
          companyName: user.user_metadata?.company_name || '',
        });

        // Load avatar URL
        setAvatarUrl(user.user_metadata?.avatar_url);

        // Load subscription data using Postgres function
        const { data: subData, error: subError } = await supabase
          .rpc('get_active_subscription', { p_user_id: user.id })
          .returns<Subscription>()
          .single();

        if (!subError && subData) {
          const typedSubData = subData as Subscription;
          setSubscription(typedSubData);

          // Get outputs used this billing period
          if (typedSubData.plan === 'starter' && typedSubData.current_period_start) {
            const { count, error: countError } = await supabase
              .from('agent_runs')
              .select('id', { count: 'exact', head: true })
              .eq('user_id', user.id)
              .gte('created_at', typedSubData.current_period_start);

            if (!countError && count !== null) {
              setOutputsUsed(count);
            }
          }
        }
      } catch (error) {
        console.error('Error loading settings data:', error);
      } finally {
        setLoading(false);
        setSubscriptionLoading(false);
      }
    };

    loadData();
  }, [user]);

  const handleProfileSave = async (data: ProfileData, file?: File, removeAvatar?: boolean) => {
    if (!user) return;

    setIsSaving(true);

    try {
      // Update user metadata with profile information
      await updateUserMetadata({
        first_name: data.firstName,
        last_name: data.lastName,
        company_name: data.companyName,
      });

      // Handle avatar removal
      if (removeAvatar) {
        await deleteAvatar();
        setAvatarUrl(undefined);
      }
      // Handle new avatar upload (but not if we just removed it)
      else if (file && file !== avatarFile) {
        const url = await uploadAvatar(file);
        await updateUserMetadata({
          avatar_url: url,
        });
        setAvatarUrl(url);
      }

      // Update local state
      setProfileData(data);
      setAvatarFile(file);

      // Show success toast
      showToast('Profile saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving profile:', error);
      showToast('Failed to save profile. Please try again.', 'error');
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = () => {
    setResetPasswordModalOpen(true);
  };

  const confirmResetPassword = async () => {
    setResetPasswordModalOpen(false);
    // Navigate first, then sign out to avoid redirect to login
    navigate('/reset-password', { state: { fromSettings: true } });
    // Sign out after a small delay to allow navigation to complete
    setTimeout(async () => {
      await signOut();
    }, 100);
  };

  const handleDeleteAccount = () => {
    setDeleteModalOpen(true);
  };

  const confirmDeleteAccount = () => {
    // TODO: Implement delete account
    showToast('Account deletion requested. You will be contacted shortly.', 'info');
    console.log('Delete account confirmed');
    setDeleteModalOpen(false);
  };

  const handleSignOut = () => {
    showToast('Signed out successfully', 'success');
    signOut();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg text-text-secondary">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-4xl mx-auto">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-2">Account/workspace, billing</p>
        </div>

        {/* Profile Section */}
        <ProfileSection
          initialData={profileData}
          currentAvatarUrl={avatarUrl}
          onSave={handleProfileSave}
          onChangePassword={handleChangePassword}
          isSaving={isSaving}
          className="mb-8"
        />

        <div className="h-px bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 my-8"></div>

        {/* Subscription Info + Account Actions Section */}
        <div className="mb-8 p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Subscription Info */}
            <SubscriptionInfo
              planName={subscription?.plan || 'Free'}
              status={subscription?.status || 'inactive'}
              nextBillingDate={
                subscription?.current_period_end
                  ? new Date(subscription.current_period_end).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })
                  : undefined
              }
              outputsUsed={subscription?.plan === 'starter' ? outputsUsed : undefined}
              outputsLimit={subscription?.plan === 'starter' ? 20 : undefined}
              loading={subscriptionLoading}
            />

            {/* Account Actions */}
            <AccountActions onDeleteAccount={handleDeleteAccount} onSignOut={handleSignOut} />
          </div>
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        size="sm"
        title="Delete Account"
      >
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            Are you sure you want to delete your account? This action cannot be undone.
          </div>

          <div className="p-3 bg-red-50 rounded border border-red-200">
            <p className="font-medium text-red-900">Warning: This will permanently delete:</p>
            <ul className="mt-2 text-sm text-red-800 list-disc list-inside">
              <li>Your profile and account data</li>
              <li>All conversations and messages</li>
              <li>All generated content</li>
              <li>Brand materials and assets</li>
            </ul>
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={confirmDeleteAccount}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Account
            </Button>
          </div>
        </div>
      </Modal>

      {/* Reset Password Confirmation Modal */}
      <Modal
        isOpen={resetPasswordModalOpen}
        onClose={() => setResetPasswordModalOpen(false)}
        size="sm"
        title="Reset Password"
      >
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            You will be logged out and redirected to the reset password page. Continue?
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => setResetPasswordModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={confirmResetPassword}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Continue
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Settings;
